import { GetServerSideProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { type InstantSearchServerState } from 'react-instantsearch'
import * as yup from 'yup'
import Layout from 'components/Layout'
import { searchIndex } from 'containers/meilisearchClient'
import SeoPages from 'containers/seoPages'
import Meta from 'containers/seoPages/Meta'
import { generateServerStateFromProps } from 'containers/seoPages/generateServerStateFromProps'
import defaultStartDate from 'domain/constants/EditionLiveStartDateTimestamp'
import {
  getBaseUrl,
  getParsedData,
  queryTypeOrDistanceSchema,
  TypeOrDistanceType
} from 'helpers/index'
import { liveRateRepository } from 'live/liveInstances'
import i18nConfig from 'next-i18next.config'
import EventService from 'services/api/database/EventService'
import { defaultFrontLocales } from 'utils/locale'

interface PageProps {
  attributesToRetrieve: string[]
  serverState?: InstantSearchServerState
  filters: string
  typeOrDistanceKey: string | null
  typeOrDistance: TypeOrDistanceType
  page?: number
  results?: Awaited<ReturnType<EventService['getGroupedEventsAmount']>>
  currencyExchangeRates: Record<string, number>
}

export default function Page({
  attributesToRetrieve,
  serverState,
  filters,
  typeOrDistanceKey,
  typeOrDistance,
  page,
  results,
  currencyExchangeRates
}: PageProps) {
  return (
    <>
      <Meta typeOrDistanceKey={typeOrDistanceKey} serverState={serverState} />

      <Layout>
        <SeoPages
          attributesToRetrieve={attributesToRetrieve}
          serverState={serverState}
          typeOrDistance={typeOrDistance}
          page={page}
          resultsByRegion={results}
          filters={filters}
          currencyExchangeRates={currencyExchangeRates}
        />
      </Layout>
    </>
  )
}

const querySchema = yup
  .object()
  .shape({
    page: yup.number()
  })
  .concat(queryTypeOrDistanceSchema)

const eventService = new EventService()
export const getServerSideProps: GetServerSideProps<PageProps> = async ({
  locale,
  query,
  res
}) => {
  const isValid = querySchema.isValidSync(query)
  if (!isValid) {
    return {
      notFound: true
    }
  }
  const { page, typeOrDistance } = querySchema.validateSync(query)

  const { typeOrDistanceAlgoliaTranslation, typeOrDistanceKey, parsedMother } =
    getParsedData({
      typeOrDistance
    })

  if (page === 1) {
    return {
      redirect: {
        destination: `${getBaseUrl(locale)}/${typeOrDistance}`,
        permanent: true
      }
    }
  }
  const groupedEventsData = await eventService.getGroupedEventsAmount({
    category: typeOrDistanceKey
  })

  const filters = typeOrDistanceAlgoliaTranslation
    ? `(editionLiveLevel2CategoryTranslation="${typeOrDistanceAlgoliaTranslation}" OR editionLiveLevel1CategoryTranslation="${typeOrDistanceAlgoliaTranslation}") AND editionLiveEndDateTimestamp>${defaultStartDate} AND eventStatus="LIVE"`
    : `editionLiveEndDateTimestamp>${defaultStartDate} AND eventStatus="LIVE"`

  const currencyExchangeRates =
    await liveRateRepository.getRunnerRatesForDefaultCurrency()

  const props = {
    ...(await serverSideTranslations(
      locale as string,
      [...defaultFrontLocales, 'event'],
      i18nConfig
    )),
    ...(page && { page }),
    typeOrDistanceKey: typeOrDistanceKey ?? null,
    filters,
    typeOrDistance,
    currencyExchangeRates,
    attributesToRetrieve: [
      'eventName',
      'eventCity',
      'eventLiveDistanceKm',
      'eventCountrySubdivisionDisplayCodeLevel2',
      'editionLiveLevel1CategoryKey',
      'eventSlug',
      'editionCalendarStatus',
      'eventLivePriceStartingFrom',
      'editionLiveStartDateTimestamp',
      'editionLiveEndDateTimestamp',
      'eventCoverImage',
      'editionLiveLevel2CategoryKey',
      'eventIsFeatured',
      'objectID',
      'eventCountry'
    ],
    results: groupedEventsData
  }

  const serverState = await generateServerStateFromProps(props)

  // Since getStaticProps cannot handle queryParams, we still need to use getServerSideProps here
  // but with cache enabled
  res.setHeader('Cache-Control', 's-maxage=86400, stale-while-revalidate=3600')

  // Example config https://github.com/algolia/react-instantsearch/blob/master/examples/next/pages/index.js

  const initialResults = serverState?.initialResults[searchIndex]?.results?.[0]

  if (initialResults?.nbHits === 0) {
    return {
      redirect: {
        destination: !parsedMother
          ? `${getBaseUrl(locale)}/`
          : `${getBaseUrl(locale)}/${parsedMother.url}`,
        permanent: true
      }
    }
  }
  if (
    typeof initialResults?.nbPages === 'number' &&
    page &&
    page > initialResults.nbPages
  ) {
    return {
      redirect: {
        destination: `${getBaseUrl(locale)}/${typeOrDistance}`,
        permanent: true
      }
    }
  }

  return {
    props: {
      ...props,
      serverState
    }
  }
}
